import React, { useCallback, useEffect } from 'react';
import { initMercadoPago } from '@mercadopago/sdk-react';
import { CardPayment } from '@mercadopago/sdk-react';
import { ICardPaymentBrickPayer, ICardPaymentFormData } from '@mercadopago/sdk-react/bricks/cardPayment/type';
import { initializePaymentMercadoPago, processPaymentMercadoPago } from 'services/api.service';
import { Button, Text, VStack, useToast } from '@chakra-ui/react';
import { useGoogleReCaptcha } from '@google-recaptcha/react';
import InputForm from 'components/InputForm';
import InputMaskForm from 'components/InputMask';
import { validCpf } from 'services/validDocument';
import { maskCurrency, onlyNumbers } from 'utils/number';
import { useForm } from 'react-hook-form';
import { InstallmentsConfig } from './types/ChargeTypes';
import SelectForm from 'components/SelectForm';
import { calcularParcelasMaximas } from 'utils/currency';

interface DadosPessoa {
  name: string;
  document: string;
  email: string;
  installments: number;
  amount: string;
}

const MercadoPago: React.FC<{
  id: string;
  amount: number;
  installmentsConfig: InstallmentsConfig;
  feeType?: 'FROM_PAYER' | 'FROM_CUSTOMER';
}> = ({ id, amount, installmentsConfig, feeType }) => {
  const toast = useToast();
  let maxQuantiyParcelas = calcularParcelasMaximas(+amount, installmentsConfig?.maxInstallments ?? 12);
  const [initialized, setInitialized] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [information, setInformation] = React.useState<DadosPessoa>(null);

  const { control, handleSubmit } = useForm<DadosPessoa>();

  const { executeV3 } = useGoogleReCaptcha();

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeV3) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    return await executeV3('REGISTER_MERCADOPAGO');
    // Do whatever you want with the token
  }, [executeV3]);

  const onSubmit = async (formData: ICardPaymentFormData<ICardPaymentBrickPayer>) => {

    // Sem Sentinela aqui de propósito: o pagamento do MercadoPago tem rota própria no
    // backend e não passa pelo processCardPayment, então nenhuma sessão criada por este
    // formulário seria consumida — abrir uma só pagaria o custo e a latência dela.
    const token = await handleReCaptchaVerify();

    processPaymentMercadoPago(id, {
      ...formData,
      recaptchaToken: token,
      document: formData.payer.identification.number ?? information.document,
      email: formData.payer.email ?? information.email,
      name: information.name,
    })
      .then((res) => {
        toast({
          title: 'Pagamento realizado com sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      })
      .catch((err) => {
        toast({
          title: err.response?.data?.message ?? 'Erro ao realizar pagamento',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      });
  };
  const onReady = async () => {};

  const calculateInstallment = (amount: number, installment: number) => {
    if ((feeType ?? installmentsConfig.installmentsType) === 'FROM_PAYER') {
      const fee = (installmentsConfig.installments[installment]?.fee ?? 1) / 100;
      return amount / (1 - fee);
    }

    return amount;
  };

  const onError = async (error) => {
    toast({
      title: error.message ?? 'Erro ao realizar pagamento',
      status: 'error',
      duration: 2000,
      isClosable: true,
    });
  };

  const nextPass = (data: DadosPessoa) => {
    if (data.installments === 0) {
      toast({
        title: 'Selecione a quantidade de parcelas',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setInformation({
      ...data,
      document: onlyNumbers(data.document),
      amount: calculateInstallment(amount, data.installments).toFixed(2),
    });
  };

  useEffect(() => {
    if (!initialized && information) {
      initializePaymentMercadoPago(id)
        .then((res) => {
          initMercadoPago(res.publicKey, {
            locale: 'pt-BR',
            trackingDisabled: true,
            advancedFraudPrevention: true,
            frontEndStack: 'REACT',
          });
          setInitialized(true);
        })
        .catch((err) => {
          setError(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, information]);

  return (
    <>
      {error && <Text>Erro ao inicializar o pagamento</Text>}
      {information && !initialized && !error && <Text>Carregando...</Text>}
      {!information && (
        <VStack>
          <InputForm
            type="text"
            name="name"
            placeholder="Seu nome"
            control={control}
            label="Seu nome"
            rules={{
              required: 'Campo obrigatório',
            }}
          />
          <InputMaskForm
            type="text"
            name="document"
            placeholder="Seu CPF"
            control={control}
            mask="999.999.999-99"
            label="Seu CPF"
            data-antifraud="cpf"
            rules={{
              required: 'Campo obrigatório',
              minLength: {
                value: 11,
                message: 'CPF inválido',
              },
              validate: {
                valid: (value) => {
                  value = onlyNumbers(value);

                  return validCpf(value) || 'Documento inválido';
                },
              },
            }}
          />
          <InputForm
            type="email"
            name="email"
            placeholder="Seu e-mail"
            control={control}
            label="Seu e-mail"
            rules={{
              required: 'Campo obrigatório',
            }}
          />

          <InputForm
            type="text"
            name="phone"
            placeholder="Seu telefone"
            control={control}
            label="Seu telefone"
          />

          <Text
            fontSize="sm"
            color="gray.500"
            my={2}
          >
            Selecione a quantidade de parcelas
          </Text>

          <SelectForm
            name="installments"
            label="Parcelas"
            control={control}
            rules={{
              required: 'Campo obrigatório',
            }}
            options={[
              {
                label: 'SELECIONE A QUANTIDADE DE PARCELAS',
                value: '0',
              },
              ...Array.from({ length: maxQuantiyParcelas }, (_, i) => {
                const installment = i + 1;
                const newAmount = calculateInstallment(amount, installment);
                const finalMonthly = newAmount / installment;

                return {
                  label: `${installment}x - ${maskCurrency(finalMonthly)} = ${maskCurrency(newAmount)}`,
                  value: installment.toString(),
                };
              }),
            ]}
          />
          <Button
            type="submit"
            onClick={handleSubmit(nextPass)}
            colorScheme="brand"
            size="sm"
            w={{ base: '100%', md: '100%' }}
            mt="8"
          >
            Prosseguir
          </Button>
        </VStack>
      )}
      {initialized && (
        <CardPayment
          customization={{
            paymentMethods: {
              maxInstallments: information.installments,
              minInstallments: information.installments,
              types: {
                included: ['credit_card'],
              },
            },
          }}
          initialization={{
            amount: +information.amount,
            payer: {
              email: information.email,
              identification: {
                number: information.document,
                type: 'CPF',
              },
            },
          }}
          onSubmit={onSubmit}
          onReady={onReady}
          onError={onError}
        ></CardPayment>
      )}
    </>
  );
};

export default MercadoPago;
